import { Popover } from '@base-ui-components/react/popover';
import { Box, TextField, Typography } from "@mui/material";
import { useState } from 'react';
import DefaultButton from '../buttons/DefaultButton';
import { requestPasswordReset } from '../../../services/user';
import { colors } from '../../../theme';
import { popupStyle, positionerStyle, closeButtonStyle, backdropStyle } from '../buttons/triggerStyles';

/**
 * Opened from the sign-in form. Deliberately reports the same thing whether or not the
 * address has an account - the server answers identically on purpose, so showing
 * "no such account" here would hand back exactly what it withholds.
 */
export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async () => {
        if (!email.trim()) {
            setError('Enter the email address you signed up with.');
            return;
        }
        setError('');
        setSending(true);
        try {
            await requestPasswordReset(email.trim());
            setSent(true);
        } catch (err: any) {
            setError(err?.message || "Couldn't send the reset email. Try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <Popover.Root
            onOpenChange={(open) => {
                if (!open) {
                    setEmail('');
                    setSent(false);
                    setError('');
                    setSending(false);
                }
            }}
        >
            <Popover.Trigger
                style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    fontSize: 13,
                    color: colors.textSecondary,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                }}
            >
                Forgot password?
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Backdrop style={backdropStyle} />
                <Popover.Positioner style={positionerStyle}>
                    <Popover.Popup style={popupStyle}>
                        <Popover.Close aria-label="Close" style={closeButtonStyle}>
                            ✕
                        </Popover.Close>

                        <Typography variant="h5" sx={{ mb: 0.5 }}>Reset your password</Typography>

                        {sent ? (
                            <Typography variant="body2" sx={{ mt: 1.5 }}>
                                If that address has an account, a reset link is on its way. The
                                link works once and expires in an hour.
                            </Typography>
                        ) : (
                            <>
                                <Typography variant="body2" sx={{ mb: 2.5 }}>
                                    We'll email you a link to choose a new one.
                                </Typography>

                                <TextField
                                    label="Email"
                                    fullWidth
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleSubmit();
                                        }
                                    }}
                                    sx={{ mb: 1.5 }}
                                />

                                {error && (
                                    <Typography sx={{ color: colors.danger, fontSize: 13, mb: 1.5 }}>
                                        {error}
                                    </Typography>
                                )}

                                <Box sx={{ mt: 2 }}>
                                    <DefaultButton
                                        text={sending ? 'Sending…' : 'Send reset link'}
                                        onClick={handleSubmit}
                                        disabled={sending}
                                        fullWidth
                                    />
                                </Box>
                            </>
                        )}
                    </Popover.Popup>
                </Popover.Positioner>
            </Popover.Portal>
        </Popover.Root>
    );
}
